import React from 'react'
import { Auth } from 'aws-amplify'
import { ErrorMessage, Field, Form, Formik, FormikActions } from 'formik'
import * as Yup from 'yup'
import * as Sentry from '@sentry/browser'
import loadingBox from '../../assets/images/loading_box.gif'
import { RouteChildrenProps } from 'react-router'
import { NotificationType } from '../../components/Notifications'
import { withNotificationContext } from '../../contexts/withNotificationContext'
import { SetNotification } from '../../contexts/NotificationProvider'
import { ITSError } from '../Layout/ITSError'

interface Props extends RouteChildrenProps<any, { emailVerified: boolean }> {
  setNotification: SetNotification
}

interface VerifyPasswordFormValues {
  username: string
  code: string
  newPassword: string
}

const VerifyPasswordFormValidationSchema = Yup.object().shape({
  username: Yup.string()
    .email('Email is not valid')
    .required('Email is required'),
  code: Yup.number().required('Code is required'),
  newPassword: Yup.string().required('Password is required'),
})

class VerifyPasswordComponent extends React.Component<Props, {}> {
  verifyPasswordFormInitialValues: VerifyPasswordFormValues = {
    username: '',
    code: '',
    newPassword: '',
  }

  componentDidMount(): void {
    if (this.props.location.state && this.props.location.state.emailVerified) {
      this.props.setNotification({
        text: 'Check your email for the verification code',
        type: NotificationType.SUCCESS,
      })
      this.props.history.replace('/verify-password', {})
    }
  }

  componentWillUnmount(): void {
    this.props.setNotification(null)
  }

  render() {
    return (
      <div className="row">
        <div className="col-md-offset-3 col-md-6">
          <div className="panel reference-box">
            <div>
              <div className="panel-heading">Enter your verification code</div>
              <div className="panel-body">
                <Formik
                  initialValues={this.verifyPasswordFormInitialValues}
                  validationSchema={VerifyPasswordFormValidationSchema}
                  onSubmit={this.handleSubmit}
                  render={({ isValid, isSubmitting }) => (
                    <Form>
                      <label htmlFor="username" className="control-label">
                        Email Address
                      </label>
                      <Field
                        id="username"
                        type="email"
                        name="username"
                        placeholder="Email"
                        className="form-control dw-input"
                      />
                      <ErrorMessage name="username" render={errorMessage => <ITSError errorMessage={errorMessage} />} />
                      <label htmlFor="code" className="control-label">
                        Verification Code
                      </label>
                      <Field
                        id="code"
                        type="text"
                        name="code"
                        placeholder="Verification Code"
                        className="form-control dw-input"
                      />
                      <ErrorMessage name="code" render={errorMessage => <ITSError errorMessage={errorMessage} />} />
                      <label htmlFor="newPassword" className="control-label">
                        Password
                      </label>
                      <Field
                        id="newPassword"
                        type="password"
                        name="newPassword"
                        placeholder="New Password"
                        className="form-control dw-input"
                      />
                      <ErrorMessage
                        name="newPassword"
                        render={errorMessage => <ITSError errorMessage={errorMessage} />}
                      />
                      {!isSubmitting ? (
                        <button type="submit" className="btn btn-default dw-btn" id="Send-btn" disabled={!isValid}>
                          Send
                        </button>
                      ) : (
                        <img src={loadingBox} className="loading" alt="loading" />
                      )}
                    </Form>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private handleSubmit = (values: VerifyPasswordFormValues, actions: FormikActions<VerifyPasswordFormValues>) => {
    Auth.forgotPasswordSubmit(values.username, values.code, values.newPassword)
      .then(() => {
        Sentry.withScope(scope => {
          scope.setLevel(Sentry.Severity.Debug)
          scope.setTag('feature', 'verify-password')
          scope.setTag('subfeature', 'success')
          scope.setTag('username', values.username)
          Sentry.captureMessage('Verify password success')
        })

        this.props.history.push('/login', { newPasswordCodeVerified: true })
      })
      .catch((err: Error) => {
        actions.setSubmitting(false)
        this.props.setNotification({
          text: 'Email or verification code is wrong',
          type: NotificationType.ERROR,
        })
        Sentry.withScope(scope => {
          scope.setLevel(Sentry.Severity.Debug)
          scope.setTag('feature', 'verify-password')
          scope.setTag('subfeature', 'failure')
          scope.setTag('username', values.username)
          Sentry.captureMessage(`${err.name ? `${err.name} - ` : ''}${err.message}`)
        })
      })
  }
}

export const VerifyPasswordPage = withNotificationContext(VerifyPasswordComponent)
